/**
 * Fetches Shopify's product recommendations into the section that asked for
 * them, once that section comes near the viewport.
 *
 * The server renders the element empty, with data-url pointing at the
 * recommendations endpoint for this same section. The response is the
 * section again, this time with `recommendations` filled in; its content
 * replaces the empty element. If there is nothing to recommend the element
 * simply stays empty, which renders nothing.
 *
 * Products another block on the page already suggests - any list marked
 * data-suggested-products, such as the flavour tiles under the buy button -
 * are left out, so the page never offers the same product twice. The
 * section asks for a couple more than it shows, and what is left is cut
 * back to data-limit, so a grid's rows stay full.
 */
class SnusifyRecommendations extends HTMLElement {
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #abort = null;
  #observer = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    if (this.dataset.state !== 'pending' || !this.dataset.url) return;

    // An empty element has no height, but a zero-height box still counts as
    // intersecting once it is inside the margin.
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  async #load() {
    this.dataset.state = 'loading';
    try {
      const response = await fetch(this.dataset.url, { signal: this.#abort.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('snusify-recommendations');
      const pathOf = SnusifyRecommendations.#pathOf;
      const suggested = new Set(
        Array.from(document.querySelectorAll('[data-suggested-products] a[href]'), pathOf)
      );
      const items = [...(fresh?.querySelectorAll('[data-recommended-items] > li') ?? [])];
      const limit = Number(this.dataset.limit) || items.length;
      let shown = 0;
      for (const item of items) {
        const link = item.querySelector('a[href]');
        if (shown >= limit || (link && suggested.has(pathOf(link)))) item.remove();
        else shown += 1;
      }
      if (!fresh?.querySelector('li')) {
        this.dataset.state = 'empty';
        return;
      }

      this.replaceChildren(...fresh.childNodes);
      this.dataset.state = 'loaded';
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.dataset.state = 'failed';
      console.error('Snusify recommendations:', error);
    }
  }

  /**
   * The product a link opens, without the tracking parameters Shopify adds
   * to recommended products' URLs.
   * @param {Element} link
   * @returns {string}
   */
  static #pathOf(link) {
    return new URL(link.getAttribute('href') ?? '', window.location.href).pathname;
  }
}

if (!customElements.get('snusify-recommendations')) {
  customElements.define('snusify-recommendations', SnusifyRecommendations);
}
